import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select, { components } from "react-select";
import "./CollegeVisitCapture.css";
import { Helmet } from "react-helmet-async";
/* ─────────────────────────────────────────────
   DATA CONSTANTS
───────────────────────────────────────────── */
const REPS = ["Srujan Vuppala", "Meena R.", "Suresh K.", "Anjali T."];

const TYPES = [
  { key: "general", ico: "🌍", label: "General" },
  { key: "university", ico: "🏛️", label: "University" },
  { key: "loan", ico: "₹", label: "Loan" },
];

// Maps the quick-add type toggle to the lead_Type.typeValue expected by the Enquiry API
const LEAD_TYPE_MAP = {
  general: "General",
  university: "University",
  loan: "Loan",
};

// ⭐ NEW — Intake month/year options for the University type's Intake fields
const MONTH_OPTIONS = [
  { monthId: 1, monthName: "January" }, { monthId: 2, monthName: "February" },
  { monthId: 3, monthName: "March" }, { monthId: 4, monthName: "April" },
  { monthId: 5, monthName: "May" }, { monthId: 6, monthName: "June" },
  { monthId: 7, monthName: "July" }, { monthId: 8, monthName: "August" },
  { monthId: 9, monthName: "September" }, { monthId: 10, monthName: "October" },
  { monthId: 11, monthName: "November" }, { monthId: 12, monthName: "December" },
];
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR + 1, CURRENT_YEAR + 2];

// ⭐ NEW — Loan type's Collateral yes/no options
const COLLATERAL_OPTIONS = ["Yes", "No"];

/* ─────────────────────────────────────────────
   COUNTRY SELECT RENDERERS (with flag icons)
───────────────────────────────────────────── */
const CountryOption = (props) => {
  const { data } = props;
  return (
    <components.Option {...props}>
      <div className="cvc-select-flex">
        <img src={`/countrylogos/${data.value}.png`} alt="" className="cvc-flag-icon" />
        <span>{data.label}</span>
      </div>
    </components.Option>
  );
};

const CountrySingleValue = (props) => {
  const { data } = props;
  return (
    <components.SingleValue {...props}>
      <div className="cvc-select-flex">
        <img src={`/countrylogos/${data.value}.png`} alt="" className="cvc-flag-icon" />
        <span>{data.label}</span>
      </div>
    </components.SingleValue>
  );
};

/* ─────────────────────────────────────────────
   COMPONENT
───────────────────────────────────────────── */
export default function CollegeVisitCapture({ logoSrc = "/logo.png" }) {
  const [online, setOnline] = useState(true);
  const [selectedType, setSelectedType] = useState("general");
  const [count, setCount] = useState(0);
  const [leads, setLeads] = useState([]);
  const [saving, setSaving] = useState(false);

  // Session info (set once per visit)
  const [collegeName, setCollegeName] = useState("");
  const [city, setCity] = useState("");
  const [visitDate, setVisitDate] = useState(new Date().toISOString().slice(0, 10));

  // Today's date (YYYY-MM-DD) — used as the min bound so past dates can't be picked
  const todayStr = new Date().toISOString().slice(0, 10);

  // Quick-add form fields (rep persists across saves — same rep usually
  // logs several leads in a row, so it is NOT reset after save)
  const [rep, setRep] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");

  // Type-specific fields
  const [destination, setDestination] = useState("");
  const [studyLevel, setStudyLevel] = useState("");
  // ⭐ NEW — General type's free-text Course field
  const [generalCourse, setGeneralCourse] = useState("");

  const [qualification, setQualification] = useState("10th");
  const [universityOfInterest, setUniversityOfInterest] = useState("");
  // ⭐ NEW — University type's free-text Course field
  const [universityCourse, setUniversityCourse] = useState("");
  // ⭐ NEW — University type's Intake month + year
  const [intakeMonth, setIntakeMonth] = useState("");
  const [intakeYear, setIntakeYear] = useState("");

  const [loanAmount, setLoanAmount] = useState("Up to ₹10 Lakhs");
  // ⭐ NEW — Loan type's Country + Collateral fields
  const [loanCountry, setLoanCountry] = useState("");
  const [hasCollateral, setHasCollateral] = useState("");

  // Consent checkbox — now controlled so it can be validated as mandatory
  const [consentGiven, setConsentGiven] = useState(true);

  // ⭐ NEW — financial data-share consent, required specifically for
  // "loan" leads (backend rejects education_loan leads without it).
  const [consentFinancialShare, setConsentFinancialShare] = useState(false);

  // Countries (for Destination Interest / Loan Country flag-selects)
  const [countries, setCountries] = useState([]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/GetCountries`);
        const trimmedCountries = response.data.map((c) => ({
          ...c,
          countryCode: c.countryCode?.trim().toLowerCase(),
        }));
        setCountries(trimmedCountries);
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };
    fetchCountries();
  }, []);

  const countryOptions = countries.map((c) => ({
    value: c.countryCode,
    label: c.countryName,
  }));

  const handleCountryChange = (selected) => {
    setDestination(selected ? selected.label : "");
  };

  // ⭐ NEW — Loan type's country select handler
  const handleLoanCountryChange = (selected) => {
    setLoanCountry(selected ? selected.label : "");
  };

  // ── Sanitizers — same pattern as other lead-capture forms ──
  const handleNameChange = (e) => {
    setName(e.target.value.replace(/[^a-zA-Z\s]/g, ""));
  };

  const handlePhoneChange = (e) => {
    setPhone(e.target.value.replace(/\D/g, "").slice(0, 10));
  };

  // ── Full mandatory validation — everything required, including consent ──
  const validateLead = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10}$/;
    const nameRegex = /^[a-zA-Z\s]+$/;

    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();
    const trimmedEmail = email.trim();
    const trimmedNotes = notes.trim();
    const trimmedCollegeName = collegeName.trim();
    const trimmedCity = city.trim();

    if (!trimmedCollegeName) {
      toast.error("Please enter the College Name for this visit.");
      return false;
    }
    if (!trimmedCity) {
      toast.error("Please enter the City for this visit.");
      return false;
    }
    if (!rep) {
      toast.error('Please select "Captured By" — required for payout tracking.');
      return false;
    }
    if (!trimmedName) {
      toast.error("Please enter the student's full name.");
      return false;
    }
    if (!nameRegex.test(trimmedName)) {
      toast.error("Name should only contain letters.");
      return false;
    }
    if (!trimmedPhone) {
      toast.error("Please enter the student's phone number.");
      return false;
    }
    if (!phoneRegex.test(trimmedPhone)) {
      toast.error("Phone number must be exactly 10 digits.");
      return false;
    }
    if (!trimmedEmail) {
      toast.error("Please enter the student's email address.");
      return false;
    }
    if (!emailRegex.test(trimmedEmail)) {
      toast.error("Please enter a valid email address.");
      return false;
    }

    if (selectedType === "general") {
      if (!destination) {
        toast.error("Please select the destination interest.");
        return false;
      }
      if (!studyLevel) {
        toast.error("Please select the study level.");
        return false;
      }
      // ⭐ NEW
      if (!generalCourse.trim()) {
        toast.error("Please enter the course of interest.");
        return false;
      }
    }

    if (selectedType === "university") {
      if (!qualification) {
        toast.error("Please select the highest qualification.");
        return false;
      }
      if (!universityOfInterest.trim()) {
        toast.error("Please enter the university of interest.");
        return false;
      }
      // ⭐ NEW
      if (!universityCourse.trim()) {
        toast.error("Please enter the course of interest.");
        return false;
      }
      if (!intakeMonth) {
        toast.error("Please select the intake month.");
        return false;
      }
      if (!intakeYear) {
        toast.error("Please select the intake year.");
        return false;
      }
    }

    if (selectedType === "loan") {
      if (!loanAmount) {
        toast.error("Please select the approximate loan amount.");
        return false;
      }
      // ⭐ NEW
      if (!loanCountry) {
        toast.error("Please select the destination country.");
        return false;
      }
      if (!hasCollateral) {
        toast.error("Please select whether collateral is available.");
        return false;
      }
      // ⭐ NEW — mandatory for loan leads specifically
      if (!consentFinancialShare) {
        toast.error("Financial data-share consent is required for loan leads.");
        return false;
      }
    }

    if (!trimmedNotes) {
      toast.error("Please add a note for this lead.");
      return false;
    }

    if (!consentGiven) {
      toast.error("Consent checkbox must be checked to save this lead.");
      return false;
    }

    return true;
  };

  const saveLead = async () => {
    if (!validateLead()) return;

    const trimmedName = name.trim();
    const trimmedPhone = phone.trim();

    const initials = trimmedName
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

    // ⭐ NEW — resolves which "Course" text applies to this lead, and the
    // University intake object, depending on the selected type.
    const resolvedCourse =
      selectedType === "general" ? generalCourse.trim()
      : selectedType === "university" ? universityCourse.trim()
      : "";

    const selectedIntake =
      selectedType === "university" && intakeMonth && intakeYear
        ? {
            monthId: Number(intakeMonth),
            monthName: MONTH_OPTIONS.find((m) => m.monthId === Number(intakeMonth))?.monthName || "",
            year: Number(intakeYear),
          }
        : null;

    const payload = {
      Enquiry_Id: null,
      CompanyId: {
        CompanyID: window.location.origin === "https://flyurdream.com" ? 160 : 2,
        UCompanyId: window.location.origin === "https://flyurdream.com" ? "GuidP160" : "GuidP2",
      },
      UCompanyId: window.location.origin === "https://flyurdream.com" ? "GuidP160" : "GuidP2",
      FirstName: trimmedName,
      LastName: "",
      Mobile_Number: trimmedPhone,
      Country_Code: "+91",
      Email: email.trim(),
      Preferred_Contact_Time: null,
      Lead_Type: { FieldType: LEAD_TYPE_MAP[selectedType] },
      Lead_Stage: { FieldType: "New" },
      Source: {
        fieldType: "College Visit"
      },
      Source_Company: "FlyUrDream",
      CollegeVisit_CollegeName: collegeName,
      CollegeVisit_City: city,
      CollegeVisit_Date: visitDate,
      Batch: "",
      Captured_By: null,
      Captured_By_System: `FieldCapture - ${rep}`,
      Assigned_To: null,
      Converted_By: null,
      Converted_Application_Id: null,
      Is_Consented: consentGiven,
      Consent_Via: null,
      // ⭐ NEW — only meaningful/required for loan leads; null otherwise
      Consent_Financial_Share: selectedType === "loan" ? consentFinancialShare : null,
      Consent_WhatsAppUpdates: true,
      // ⭐ CHANGED — loan leads now also carry a Preferred_CountryName (their
      // destination country), same field general leads already used.
      Preferred_Country: null,
      Preferred_CountryName:
        selectedType === "general" ? destination
        : selectedType === "loan" ? loanCountry
        : "",
      // ⭐ NEW — Course_Interest now populated for both general and university leads
      Course_Interest: resolvedCourse,
      Study_Level: selectedType === "general" ? studyLevel : "",
      University: null,
      University_Interest: selectedType === "university" ? universityOfInterest : "",
      // ⭐ NEW — Intake now populated for university leads
      Intake: selectedIntake,
      Loan_Amount_Range:
        selectedType === "loan" ? { FieldType: loanAmount } : null,
      // ⭐ NEW — Collateral, loan leads only
      Has_Collateral: selectedType === "loan" ? hasCollateral === "Yes" : null,
      Notes: notes.trim(),
      CreatedBy: null,
      IsArchived: false,
    };

    // Backend expects the payload wrapped under an "enquiry" key
    const requestBody = { ...payload };

    const newLead = {
      id: Date.now(),
      initials,
      name: trimmedName,
      phone: trimmedPhone,
      rep,
      synced: false,
    };

    setSaving(true);
    try {
      if (online) {
        const response = await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/CreateEnquiry`,
          requestBody
        );
        console.log("Enquiry created:", response.data);
        newLead.synced = true;
        toast.success("Lead saved — added to FlyUrDream Enquiries");
      } else {
        // Offline — queue locally, mark unsynced. A background sync job
        // (not implemented here) should retry these when connectivity returns.
        console.log("Offline — queued payload:", payload);
        newLead.synced = false;
        toast.success("Lead saved locally — will sync once you're back online");
      }
    } catch (error) {
      console.error("Enquiry submit failed:", error);
      newLead.synced = false;
      toast.error("Could not sync this lead right now — it's saved locally and will retry.");
    } finally {
      setSaving(false);
    }

    setCount((c) => c + 1);
    setLeads((prev) => [newLead, ...prev]);

    // Reset form (rep selection persists — same rep usually logs several leads in a row)
    setName("");
    setPhone("");
    setEmail("");
    setNotes("");
    setDestination("");
    setStudyLevel("");
    setGeneralCourse(""); // ⭐ NEW
    setQualification("10th");
    setUniversityOfInterest("");
    setUniversityCourse(""); // ⭐ NEW
    setIntakeMonth(""); // ⭐ NEW
    setIntakeYear(""); // ⭐ NEW
    setLoanAmount("Up to ₹10 Lakhs");
    setLoanCountry(""); // ⭐ NEW
    setHasCollateral(""); // ⭐ NEW
    setConsentGiven(true);
    setConsentFinancialShare(false); // ⭐ NEW — reset for next lead

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="cvc-page">
      {/* ── TOPBAR ── */}
      {/* <div className="cvc-topbar">
        <img
          src={logoSrc}
          alt="FlyUrDream"
          style={{ filter: "brightness(0) invert(1)" }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
        <span className="cvc-topbar-title">FIELD LEAD CAPTURE</span>
        <button
          type="button"
          className={`cvc-offline-pill${online ? " cvc-online" : ""}`}
          onClick={() => setOnline((o) => !o)}
        >
          <span className="cvc-dot" />
          {online ? "Online · Syncing" : "Offline · Queued"}
        </button>
      </div> */}
<Helmet>
        <title>Field Lead Capture | FlyUrDream</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="cvc-wrap">
        {/* ── SESSION INFO ── */}
        <div className="cvc-session-card">
          <h2>📍 College Visit Session</h2>
          <p>Set this once per visit — applies to all leads captured today</p>
          <div className="cvc-session-fields">
            <input
              type="text"
              placeholder="College Name"
              value={collegeName}
              onChange={(e) => setCollegeName(e.target.value)}
            />
            <input
              type="text"
              placeholder="City"
              value={city}
              onChange={(e) => setCity(e.target.value)}
            />
            <input
              type="date"
              value={visitDate}
              onChange={(e) => setVisitDate(e.target.value)}
              min={todayStr}
              style={{ gridColumn: "1/3" }}
            />
          </div>
        </div>

        {/* ── QUICK ADD FORM ── */}
        <div className="cvc-card">
          <h3>+ Add New Lead</h3>
          <div className="cvc-card-sub">Keep it fast — full details collected when they apply</div>

          <div className="cvc-f-row">
            <label>CAPTURED BY <span className="cvc-req">*</span></label>
            <select value={rep} onChange={(e) => setRep(e.target.value)}>
              <option value="">Select rep — required for payout tracking</option>
              {REPS.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
          </div>

          <div className="cvc-type-toggle">
            {TYPES.map((t) => (
              <button
                type="button"
                key={t.key}
                className={`cvc-type-btn${selectedType === t.key ? " cvc-sel" : ""}`}
                onClick={() => setSelectedType(t.key)}
              >
                <span className="cvc-tico">{t.ico}</span>
                {t.label}
              </button>
            ))}
          </div>

          <div className="cvc-f-row">
            <label>FULL NAME <span className="cvc-req">*</span></label>
            <input
              type="text"
              placeholder="Student's full name"
              value={name}
              onChange={handleNameChange}
            />
          </div>
          <div className="cvc-f-row">
            <label>PHONE <span className="cvc-req">*</span></label>
            <div className="cvc-phone-grid">
              <select defaultValue="+91"><option>+91</option></select>
              <input
                type="text"
                placeholder="98765 43210"
                value={phone}
                onChange={handlePhoneChange}
                inputMode="numeric"
                maxLength={10}
              />
            </div>
          </div>
          <div className="cvc-f-row">
            <label>EMAIL <span className="cvc-req">*</span></label>
            <input
              type="email"
              placeholder="student@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          {selectedType === "general" && (
            <div className="cvc-type-box">
              <div className="cvc-type-box-label">GENERAL FIELDS</div>
              <div className="cvc-f-row">
                <label>DESTINATION INTEREST <span className="cvc-req">*</span></label>
                <Select
                  classNamePrefix="cvc-country-select"
                  options={countryOptions}
                  components={{ Option: CountryOption, SingleValue: CountrySingleValue }}
                  value={countryOptions.find((opt) => opt.label === destination) || null}
                  onChange={handleCountryChange}
                  placeholder="Select destination"
                  isSearchable
                />
              </div>
              <div className="cvc-f-row">
                <label>STUDY LEVEL <span className="cvc-req">*</span></label>
                <select value={studyLevel} onChange={(e) => setStudyLevel(e.target.value)}>
                  <option value="">Select level</option>
                  <option value="Foundation">Foundation</option>
                  <option value="Bachelors Degree">Bachelors Degree</option>
                  <option value="Masters Degree">Masters Degree</option>
                  <option value="Not decided">Not decided</option>
                </select>
              </div>
              {/* ⭐ NEW — General type's free-text Course field */}
              <div className="cvc-f-row">
                <label>COURSE <span className="cvc-req">*</span></label>
                <input
                  type="text"
                  placeholder="e.g. Computer Science"
                  value={generalCourse}
                  onChange={(e) => setGeneralCourse(e.target.value)}
                />
              </div>
            </div>
          )}

          {selectedType === "university" && (
            <div className="cvc-type-box">
              <div className="cvc-type-box-label">UNIVERSITY FIELDS</div>
              <div className="cvc-f-row">
                <label>HIGHEST QUALIFICATION <span className="cvc-req">*</span></label>
                <select value={qualification} onChange={(e) => setQualification(e.target.value)}>
                  <option>10th</option>
                  <option>12th</option>
                  <option>Bachelor's</option>
                </select>
              </div>
              <div className="cvc-f-row">
                <label>UNIVERSITY OF INTEREST <span className="cvc-req">*</span></label>
                <input
                  type="text"
                  placeholder="If known"
                  value={universityOfInterest}
                  onChange={(e) => setUniversityOfInterest(e.target.value)}
                />
              </div>
              {/* ⭐ NEW — University type's free-text Course field */}
              <div className="cvc-f-row">
                <label>COURSE <span className="cvc-req">*</span></label>
                <input
                  type="text"
                  placeholder="e.g. MSc Data Science"
                  value={universityCourse}
                  onChange={(e) => setUniversityCourse(e.target.value)}
                />
              </div>
              {/* ⭐ NEW — University type's Intake Month + Year */}
              <div className="cvc-f-row">
                <label>INTAKE MONTH <span className="cvc-req">*</span></label>
                <select value={intakeMonth} onChange={(e) => setIntakeMonth(e.target.value)}>
                  <option value="">Select month</option>
                  {MONTH_OPTIONS.map((m) => (
                    <option key={m.monthId} value={m.monthId}>{m.monthName}</option>
                  ))}
                </select>
              </div>
              <div className="cvc-f-row">
                <label>INTAKE YEAR <span className="cvc-req">*</span></label>
                <select value={intakeYear} onChange={(e) => setIntakeYear(e.target.value)}>
                  <option value="">Select year</option>
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {selectedType === "loan" && (
            <div className="cvc-type-box">
              <div className="cvc-type-box-label">LOAN FIELDS</div>
              <div className="cvc-f-row">
                <label>LOAN AMOUNT (APPROX) <span className="cvc-req">*</span></label>
                <select value={loanAmount} onChange={(e) => setLoanAmount(e.target.value)}>
                  <option>Up to ₹10 Lakhs</option>
                  <option>₹10–25 Lakhs</option>
                  <option>₹25–50 Lakhs</option>
                  <option>₹50 Lakhs – ₹1 Crore</option>
                  <option>₹1–2 Crore</option>
                  <option>₹2 Crore+</option>
                </select>
              </div>

              {/* ⭐ NEW — Loan type's destination Country field */}
              <div className="cvc-f-row">
                <label>COUNTRY <span className="cvc-req">*</span></label>
                <Select
                  classNamePrefix="cvc-country-select"
                  options={countryOptions}
                  components={{ Option: CountryOption, SingleValue: CountrySingleValue }}
                  value={countryOptions.find((opt) => opt.label === loanCountry) || null}
                  onChange={handleLoanCountryChange}
                  placeholder="Select destination country"
                  isSearchable
                />
              </div>

              {/* ⭐ NEW — Loan type's Collateral yes/no field */}
              <div className="cvc-f-row">
                <label>COLLATERAL AVAILABLE <span className="cvc-req">*</span></label>
                <select value={hasCollateral} onChange={(e) => setHasCollateral(e.target.value)}>
                  <option value="">Select</option>
                  {COLLATERAL_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {/* ⭐ NEW — required for loan leads; backend rejects
                  education_loan leads without this consent set */}
              <div className="cvc-consent-row">
                <input
                  type="checkbox"
                  id="cvc-consent-financial"
                  checked={consentFinancialShare}
                  onChange={(e) => setConsentFinancialShare(e.target.checked)}
                />
                <label htmlFor="cvc-consent-financial">
                  Student consents to sharing financial/personal details with partner banks/NBFCs for loan processing. <span className="cvc-req">*</span>
                </label>
              </div>
            </div>
          )}

          <div className="cvc-f-row">
            <label>NOTES <span className="cvc-req">*</span></label>
            <textarea
              rows={2}
              placeholder="Anything else worth noting..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>

          <div className="cvc-consent-block">
            <div className="cvc-consent-row">
              <input
                type="checkbox"
                id="cvc-consent-main"
                checked={consentGiven}
                onChange={(e) => setConsentGiven(e.target.checked)}
              />
              <label htmlFor="cvc-consent-main">
                Student verbally consented to be contacted by FlyUrDream via email/phone/WhatsApp about courses and services. <span className="cvc-req">*</span>
              </label>
            </div>
          </div>

          <button type="button" className="cvc-btn-save" onClick={saveLead} disabled={saving}>
            {saving ? "Saving..." : "✓ Save Lead"}
          </button>
        </div>
      </div>

      <ToastContainer />
    </div>
  );
}